/**
 * WebSocket client — Socket.io connection with auth, subscribe/unsubscribe,
 * command sending, and event listening.
 */

import { io, type Socket } from 'socket.io-client';
import { getToken, refreshToken, isTokenExpiringSoon } from './auth';
import type { WsCommand } from '@/types';

/**
 * Resolve the Socket.io URL.
 * - VITE_WS_URL set (dev): use it directly (e.g. ws://localhost:3001/ws)
 * - Production: use '/ws' namespace relative to current origin
 *   → browser connects to wss://admin.example.net/ws/
 *   → nginx proxies /ws/ to core:3001/
 */
const resolveWsUrl = (): string => {
  const envUrl = import.meta.env.VITE_WS_URL;
  if (envUrl) return envUrl;
  if (typeof window !== 'undefined') return window.location.origin;
  return '/';
};

class WsClient {
  private socket: Socket | null = null;
  private listeners = new Map<string, Set<(...args: unknown[]) => void>>();
  private topics = new Set<string>();
  /** In-flight connection shared by concurrent connect() callers. */
  private connecting: Promise<Socket> | null = null;

  /**
   * Connect to the WebSocket gateway with auth token. Concurrent callers share
   * a single in-flight connection (the promise is cleared once it settles) so
   * multiple providers/pages never open duplicate sockets.
   */
  async connect(): Promise<Socket> {
    if (this.socket?.connected) return this.socket;
    if (this.connecting) return this.connecting;

    this.connecting = this.openConnection();
    try {
      return await this.connecting;
    } finally {
      this.connecting = null;
    }
  }

  /** Establish the socket and wire its lifecycle handlers. */
  private async openConnection(): Promise<Socket> {
    // Refresh token if expiring soon (within 60 seconds)
    if (isTokenExpiringSoon(60)) {
      try {
        console.log('[WS] Token expiring soon, refreshing...');
        await refreshToken();
      } catch (err) {
        console.error('[WS] Token refresh failed:', err);
        // Continue with existing token, server will reject if expired
      }
    }

    const token = getToken();
    const wsUrl = resolveWsUrl();
    console.log('[WS] Connecting to:', wsUrl);
    this.socket = io(wsUrl, {
      auth: { token, userId: 'admin-ui' },
      path: '/ws/socket.io/',
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 10000,
    });

    this.socket.on('connect', () => {
      console.log('[WS] Connected:', this.socket?.id);
      this.reconnectSubscriptions();
      this.emit('connected', {});
    });

    this.socket.on('disconnect', (reason) => {
      console.log('[WS] Disconnected:', reason);
      this.emit('disconnected', { reason });
    });

    this.socket.on('connect_error', async (err) => {
      console.error('[WS] Connection error:', err.message);
      // If error is due to expired token, try to refresh and reconnect
      if (err.message.includes('Invalid authentication token') || err.message.includes('jwt expired')) {
        try {
          console.log('[WS] Token expired, refreshing and reconnecting...');
          await refreshToken();
          this.socket?.disconnect();
          this.socket = null;
          await this.connect();
        } catch (refreshErr) {
          console.error('[WS] Token refresh failed:', refreshErr);
        }
      }
      this.emit('error', { message: err.message });
    });

    // Re-emit all server events through the listener map
    this.socket.onAny((event: string, ...args: unknown[]) => {
      console.log('[WS] Received event from server:', event, args);
      this.emit(event, ...args);
    });

    return this.socket;
  }

  /** Disconnect the socket. */
  disconnect(): void {
    this.socket?.disconnect();
    this.socket = null;
    this.listeners.clear();
  }

  /** Check if connected. */
  get connected(): boolean {
    return this.socket?.connected ?? false;
  }

  /** Subscribe to a topic/room. */
  subscribe(topic: string): Promise<unknown> {
    this.topics.add(topic);
    return new Promise((resolve, reject) => {
      try {
        this.ensureConnected();
      } catch (err) {
        // Not connected yet — subscription will be sent on (re)connect.
        resolve(undefined);
        return;
      }
      console.log('[WS] Subscribing to topic:', topic);
      this.socket!.emit('subscribe', { topic }, (ack: unknown) => {
        console.log('[WS] Subscribe acknowledgment:', ack);
        resolve(ack);
      });
    });
  }

  /** Unsubscribe from a topic/room. */
  unsubscribe(topic: string): Promise<unknown> {
    this.topics.delete(topic);
    return new Promise((resolve, reject) => {
      try {
        this.ensureConnected();
      } catch (err) {
        resolve(undefined);
        return;
      }
      this.socket!.emit('unsubscribe', { topic }, (ack: unknown) => {
        resolve(ack);
      });
    });
  }

  /**
   * Re-emit a 'subscribe' for every active topic. Called after a socket.io
   * (re)connection so that rooms subscribed before the reconnect are restored.
   * @private
   */
  private reconnectSubscriptions(): void {
    this.topics.forEach((topic) => {
      console.log('[WS] Re-subscribing to topic:', topic);
      this.socket?.emit('subscribe', { topic }, () => {});
    });
  }

  /** Send a command to a backend service. */
  sendCommand(command: WsCommand): Promise<unknown> {
    return new Promise((resolve, reject) => {
      this.ensureConnected();
      this.socket!.emit('command', command, (ack: unknown) => {
        resolve(ack);
      });
    });
  }

  /** Refresh the auth token without reconnecting. */
  refreshAuthToken(token: string): void {
    this.ensureConnected();
    this.socket!.emit('token:refresh', { token });
  }

  /** Listen for an event. Returns unsubscribe function. */
  on(event: string, callback: (...args: unknown[]) => void): () => void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)!.add(callback);
    return () => {
      this.listeners.get(event)?.delete(callback);
    };
  }

  /** Emit to local listeners. */
  private emit(event: string, ...args: unknown[]): void {
    const cbs = this.listeners.get(event);
    if (cbs) {
      for (const cb of cbs) {
        try {
          cb(...args);
        } catch (err) {
          console.error(`[WS] Listener error for "${event}":`, err);
        }
      }
    }
  }

  private ensureConnected(): void {
    if (!this.socket?.connected) {
      throw new Error('WebSocket not connected. Call connect() first.');
    }
  }
}

/** Singleton WebSocket client instance. */
export const wsClient = new WsClient();
